import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { DEFECT_TYPES, DEFECT_TYPE_COLORS, formatEUR } from "@/lib/dict";
import { TrendingUp } from "lucide-react";

const SEVERITY_COLORS = { minor: "#94A3B8", major: "#F59E0B", critical: "#EF4444" };

export default function TimelineStats() {
  const { data } = useQuery({ queryKey: ["stats-timeline"], queryFn: () => api.get("/stats/timeline?months=12").then((r) => r.data) });
  if (!data) return null;
  const timeline = data.timeline.map((t) => ({ ...t, label: t.month.slice(5) + "/" + t.month.slice(2, 4) }));
  const typeData = data.by_type.slice(0, 6).map((t) => ({ ...t, label: DEFECT_TYPES[t.name] || t.name, color: DEFECT_TYPE_COLORS[t.name] || "#6B7280" }));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><TrendingUp size={12} /> Statistiques</div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-1">Évolution 12 mois</h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200">
          <div className="font-display font-semibold text-slate-900 mb-1">Désordres &amp; sinistres par mois</div>
          <div className="text-xs text-slate-500 mb-4">Nombre d'entités créées</div>
          <div className="h-64" data-testid="chart-monthly">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeline} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="defects" fill="#023E73" radius={[3, 3, 0, 0]} name="Désordres" />
                <Bar dataKey="claims" fill="#EF4444" radius={[3, 3, 0, 0]} name="Sinistres" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="font-display font-semibold text-slate-900 mb-1">Coûts estimés vs indemnisations</div>
          <div className="text-xs text-slate-500 mb-4">Cumul par mois (EUR)</div>
          <div className="h-64" data-testid="chart-costs">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => formatEUR(v)} />
                <Line type="monotone" dataKey="estimated_cost" stroke="#023E73" strokeWidth={2} dot={{ r: 3 }} name="Coût estimé" />
                <Line type="monotone" dataKey="compensation" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Indemnisation" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="font-display font-semibold text-slate-900 mb-1">Désordres par type</div>
          <div className="text-xs text-slate-500 mb-4">Répartition top 6</div>
          <div className="h-64" data-testid="chart-types">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeData} layout="vertical" margin={{ top: 5, right: 20, left: 90, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                  {typeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="font-display font-semibold text-slate-900 mb-1">Répartition par gravité</div>
          <div className="text-xs text-slate-500 mb-4">Ensemble des désordres</div>
          <div className="h-64" data-testid="chart-severity">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_severity} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {data.by_severity.map((entry, i) => <Cell key={i} fill={SEVERITY_COLORS[entry.name] || "#6B7280"} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v === "minor" ? "Mineur" : v === "major" ? "Majeur" : "Critique"} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
